import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { AuthProvider } from './lib/auth.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { ApiError } from './lib/api.js';
import { applyThemeChoice, readThemeChoice } from './lib/theme.js';
import { следитьЗаНовойВерсией } from './lib/obnovlenie.js';
import './styles/global.css';

/*
 * Тема применяется до первой отрисовки.
 *
 * Если сделать это внутри React, страница на мгновение мигнёт светлым, пока
 * приложение поднимается, — заметнее всего это ночью, и выглядит как дефект.
 */
applyThemeChoice(readThemeChoice());

/*
 * Установленное приложение обязано узнавать о починках само.
 *
 * Иначе выкладка доходит только до тех, кто открывает сайт в браузере,
 * а у остальных «ничего не изменилось» — и это правда.
 */
следитьЗаНовойВерсией();

/**
 * Точка входа фронтенда.
 *
 * Политика повторов подобрана под мобильный интернет: временный обрыв связи
 * лечится повтором, а вот 401 и 404 повторять бессмысленно — ответ не изменится,
 * и повтор только затянет показ ошибки.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
      // Возврат на вкладку — хороший момент обновить список: вдруг подарок уже забрали.
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Не найден корневой элемент #root');

createRoot(container).render(
  <StrictMode>
    {/*
      Перехватчик снаружи всего: он должен пережить падение любой части
      приложения, включая маршрутизацию и загрузку данных.
    */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
